import type { LocalizedText } from "../../../app/i18n/i18n";
import { isBottomAnchored, type DocumentLayoutBlock } from "../documentAppearance/documentAppearanceApi";

/**
 * Was Textpflege und Einzelversand gemeinsam über die Bausteine eines Systemdokuments wissen
 * müssen. Beide zeigen dasselbe Blatt über `DocumentBlockEditor`; sie unterscheiden sich allein
 * darin, **wohin** eine Änderung geschrieben wird — in den Textsatz oder in eine einmalige
 * Abweichung für genau diese Sendung.
 */

/** Bezeichnung eines Bausteintyps; dieselben Namen wie im Erscheinungsbild. */
export function systemDocumentBlockLabel(type: string): LocalizedText {
  const labels: Record<string, LocalizedText> = {
    logo: "Logo",
    sender: "Sender line",
    address: "Address field",
    date: "Date line",
    subject: "Subject",
    salutation: "Salutation",
    introduction: "Introduction",
    notice: "Notice text",
    closing: "Closing formula",
    footer: "Footer",
    text: "Fixed text",
    divider: "Divider",
    spacer: "Spacing",
  };
  return labels[type] ?? type;
}

/** Fußbereich und Blattfuß gehören als gemeinsamer Satzspiegel ausschließlich zum Erscheinungsbild. */
export function isAppearanceOwnedBlock(block: DocumentLayoutBlock): boolean {
  return block.type === "footer" || block.type === "pageNumber" || isBottomAnchored(block);
}

/**
 * Die pflegbaren Textfelder eines Systemdokuments in der Reihenfolge, in der sie im Schreiben
 * stehen. Ein Baustein dieses Typs trägt den Wortlaut genau dieses Feldes — das ist die Brücke
 * zwischen dem Blatt und dem, was Vorschau und Versand als Text entgegennehmen.
 */
export const SYSTEM_DOCUMENT_TEXT_BLOCK_FIELDS = {
  subject: "subject",
  salutation: "salutation",
  introduction: "introduction",
  notice: "notice",
  closing: "closing",
} as const;

export type SystemDocumentTextBlockField =
  (typeof SYSTEM_DOCUMENT_TEXT_BLOCK_FIELDS)[keyof typeof SYSTEM_DOCUMENT_TEXT_BLOCK_FIELDS];

/**
 * Das Textfeld hinter einem Baustein — oder `null`, wenn der Baustein keinen pflegbaren Wortlaut
 * trägt. Anschrift, Datumszeile, Logo und Fuß entstehen aus Stammdaten und dem Format; sie sind
 * kein Text, den ein Einzelversand ändern könnte.
 */
export function systemDocumentTextFieldOfBlock(
  block: DocumentLayoutBlock | null
): SystemDocumentTextBlockField | null {
  if (block == null || isAppearanceOwnedBlock(block)) return null;
  const field = SYSTEM_DOCUMENT_TEXT_BLOCK_FIELDS[block.type as SystemDocumentTextBlockField];
  return field ?? null;
}

/**
 * Was ein **Inhalt** an einem Baustein ändern darf — gleichgültig, ob der Inhalt ein gepflegter
 * Textsatz ist oder eine einzelne Sendung. Beide Editoren fragen dieselbe Funktion; zwei Listen
 * mit denselben Bausteintypen wären die Stelle, an der sie irgendwann auseinanderlaufen.
 *
 * Die Regel dahinter: Ein Inhalt schreibt und zeichnet aus. Was das **Blatt** ausmacht — Logo,
 * Trennlinie, Abstand und der untere Satzspiegel aus Fuß und Seitenzahl — gehört dem
 * Erscheinungsbild und ist auch aus dem Textmodus heraus nicht änderbar; das schließt das
 * Ausblenden ein. Eine ausgeblendete Trennlinie wäre eine Gestaltungsänderung, die nur für einen
 * Textsatz gilt, und damit genau die zweite Gestaltungsebene, die es nicht geben soll.
 */
export function systemDocumentBlockCapabilities(block: DocumentLayoutBlock): {
  /** Wortlaut schreiben. */
  writable: boolean;
  /** Zeichenformat setzen. */
  formattable: boolean;
  /** Über das Auge weglassen. */
  hideable: boolean;
  /** Das Logo hat eine eigene Darstellung in der Fläche. */
  logo: boolean;
} {
  const layoutOnly = isAppearanceOwnedBlock(block)
    || block.type === "logo"
    || block.type === "divider"
    || block.type === "spacer";
  return {
    writable: !layoutOnly,
    formattable: !layoutOnly,
    hideable: !layoutOnly,
    logo: block.type === "logo",
  };
}
